"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * "SEE BITEBALANCE IN ACTION" (item 9): the illustrated app-shell demo from
 * main.html. ONE interactive panel whose five surfaces the visitor switches
 * between — the concept, the shell and the tab interaction are unchanged.
 *
 * Parity correction 2026-09-10 (founder-approved, see
 * docs/Marketing/homepage-product-parity-audit-2026-09-10.md): every panel now
 * shows a state the product actually produces, curated under the standing rule
 * **reveal the experience, protect the machinery**.
 *  - Today: the day's meals with kcal and cook time, and one quiet "planned for
 *    today" line — no eaten/target accounting bar, no macro chips, no outcome
 *    pills. The invented green "Amy suggests" banner is gone; Amy's real entry
 *    point is in the rail, exactly where the app puts it (LeftRail/BottomNav).
 *  - Plan: the day strip and the day's meals — the shape of the week, not its
 *    operational controls.
 *  - Kitchen: "we know what you already have", not an inventory ledger — no
 *    quantities, units, steppers or expiry lines. (Consumer-facing name is
 *    Kitchen on every platform, founder decision 2026-09-10; the in-product
 *    label and the internal route/model names are unchanged and belong to T21.)
 *  - Shop: "your list already accounts for what you have" — no estimate total,
 *    price mix, per-item prices or purchase-state chips.
 *  - Insights: two numbers and a sentence, not the reporting interface. The
 *    seven-bar weekly chart is removed: no such chart exists in the product.
 *
 * Every figure is copied from the recorded 10 September plan on the isolated
 * marketing test account (packages/contracts/fixtures/marketing/). This
 * component never computes a product number.
 */

import { useState } from "react"
import { CalendarDays, Home, Refrigerator, ShoppingCart, Sparkles, TrendingUp } from "lucide-react"
import { useI18n } from "@/i18n"
import { BrandMarkV2 } from "./BrandMarkV2"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { Reveal } from "./Reveal"

const TABS = [
  { id: "today", labelKey: "landingPage.demo.navToday", icon: Home },
  { id: "plan", labelKey: "landingPage.demo.navPlan", icon: CalendarDays },
  { id: "kitchen", labelKey: "landingPage.demo.navKitchen", icon: Refrigerator },
  { id: "shop", labelKey: "landingPage.demo.navShop", icon: ShoppingCart },
  { id: "insights", labelKey: "landingPage.demo.navInsights", icon: TrendingUp },
] as const

/** The real MealCard reads slot → title → chips. Marketing keeps that order. */
function MealRow({ slot, title, kcal, time, badge }: { slot: string; title: string; kcal?: string; time: string; badge?: string }) {
  return (
    <div className="rounded-bb-md border border-line bg-cream p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-bold tracking-wide text-green-2 uppercase">{slot}</p>
          <h3 className="text-sm leading-snug font-semibold text-ink">{title}</h3>
        </div>
        {badge && (
          <span className="shrink-0 rounded-bb-full bg-sky/40 px-2 py-0.5 text-[10px] font-bold text-ink-2">
            {badge}
          </span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {kcal && (
          <span className="rounded-bb-full border border-green/25 bg-lime/35 px-2 py-0.5 text-[11px] font-semibold text-ink">
            {kcal}
          </span>
        )}
        <span className="rounded-bb-full border border-line bg-paper px-2 py-0.5 text-[11px] text-ink-2">
          {time}
        </span>
      </div>
    </div>
  )
}

function PanelHead({ eyebrow, title, sub }: { eyebrow: string; title: string; sub?: string }) {
  return (
    <>
      <p className="text-[11px] font-bold tracking-wide text-ink-2 uppercase">{eyebrow}</p>
      <p className="font-display mt-0.5 text-xl font-semibold text-ink">{title}</p>
      {sub && <p className="mt-1 text-xs text-ink-2">{sub}</p>}
    </>
  )
}

function TodayPanel() {
  const { t } = useI18n()
  const meals = [
    ["slotBreakfast", "mBreakfast", "mBreakfastKcal", "mBreakfastTime"],
    ["slotLunch", "mLunch", "mLunchKcal", "mLunchTime"],
    ["slotAfternoon", "mAfternoon", "mAfternoonKcal", "mAfternoonTime"],
    ["slotDinner", "mDinner", "mDinnerKcal", "mDinnerTime"],
  ] as const
  return (
    <>
      <p className="text-[11px] font-bold tracking-[0.13em] text-green-2 uppercase">
        {t("landingPage.demo.tDate")}
      </p>
      <p className="font-display mt-1 text-2xl font-semibold text-ink">
        {t("landingPage.demo.tGreeting")}
      </p>
      <p className="mt-1 text-xs text-ink-2">{t("landingPage.demo.tSub")}</p>
      <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
        {meals.map(([slot, title, kcal, time]) => (
          <MealRow
            key={title}
            slot={t(`landingPage.demo.${slot}`)}
            title={t(`landingPage.demo.${title}`)}
            kcal={t(`landingPage.demo.${kcal}`)}
            time={t(`landingPage.demo.${time}`)}
          />
        ))}
      </div>
      <p className="mt-3 text-xs text-ink-2">{t("landingPage.demo.tPlanned")}</p>
    </>
  )
}

/** The real Plan is a day strip plus the selected day. Marketing keeps the
 * shape and drops the per-meal controls. */
function PlanPanel() {
  const { t, locale } = useI18n()
  const [day, setDay] = useState(0)
  const fmt = new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-GB", { weekday: "short" })
  return (
    <>
      <PanelHead
        eyebrow={t("landingPage.demo.navPlan")}
        title={t("landingPage.demo.pTitle")}
      />
      <div className="mt-4 flex gap-1.5" role="tablist" aria-label={t("landingPage.demo.pTitle")}>
        {Array.from({ length: 7 }, (_, i) => {
          const date = new Date(Date.UTC(2026, 8, 10 + i))
          const isActive = day === i
          return (
            <button
              key={i}
              role="tab"
              type="button"
              aria-selected={isActive}
              onClick={() => setDay(i)}
              className={`min-h-14 flex-1 rounded-bb-md border py-2 text-center transition-all duration-200 ${
                isActive
                  ? "border-green bg-green text-paper shadow-sm"
                  : "border-line bg-cream text-ink-2 hover:border-green/40"
              } ${i === 0 && !isActive ? "ring-2 ring-lime/70" : ""}`}
            >
              <span
                className={`block text-[10px] font-bold tracking-wide uppercase ${isActive ? "text-lime" : "text-ink-2"}`}
              >
                {fmt.format(date)}
              </span>
              <span className="block text-base leading-tight font-bold">{10 + i}</span>
            </button>
          )
        })}
      </div>
      <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
        <MealRow
          slot={t("landingPage.demo.slotBreakfast")}
          title={t("landingPage.demo.mBreakfast")}
          time={t("landingPage.demo.mBreakfastTime")}
        />
        <MealRow
          slot={t("landingPage.demo.slotLunch")}
          title={t("landingPage.demo.mLunch")}
          time={t("landingPage.demo.mLunchTime")}
        />
        <MealRow
          slot={t("landingPage.demo.slotAfternoon")}
          title={t("landingPage.demo.mAfternoon")}
          time={t("landingPage.demo.mAfternoonTime")}
        />
        <MealRow
          slot={t("landingPage.demo.slotDinner")}
          title={t("landingPage.demo.mDinner")}
          time={t("landingPage.demo.mDinnerTime")}
          badge={t("landingPage.demo.pBatch")}
        />
      </div>
      <p className="mt-3 text-xs text-ink-2">{t("landingPage.demo.pFoot")}</p>
    </>
  )
}

function KitchenPanel() {
  const { t } = useI18n()
  const items = [1, 2, 3, 4].map(i => t(`landingPage.demo.kItem${i}`))
  return (
    <>
      <PanelHead
        eyebrow={t("landingPage.demo.navKitchen")}
        title={t("landingPage.demo.kTitle")}
        sub={t("landingPage.demo.kSub")}
      />
      <ul className="mt-4 flex flex-col gap-2">
        {items.map(item => (
          <li
            key={item}
            className="flex items-center gap-3 rounded-bb-md border border-line bg-cream p-3"
          >
            <span aria-hidden="true" className="h-5 w-1 shrink-0 rounded-bb-full bg-lime" />
            <span className="text-sm font-semibold text-ink capitalize">{item}</span>
            <span className="ml-auto text-[11px] font-semibold text-green-2">
              {t("landingPage.demo.kInStock")}
            </span>
          </li>
        ))}
      </ul>
    </>
  )
}

function ShopPanel() {
  const { t } = useI18n()
  const [checked, setChecked] = useState<boolean[]>([true, false, false])
  const items = [1, 2, 3].map(i => ({
    label: t(`landingPage.demo.sItem${i}`),
    buy: t(`landingPage.demo.sBuy${i}`),
  }))
  return (
    <>
      <PanelHead
        eyebrow={t("landingPage.demo.navShop")}
        title={t("landingPage.demo.sTitle")}
        sub={t("landingPage.demo.sSub")}
      />
      <ul className="mt-4 flex flex-col gap-2">
        {items.map((item, i) => (
          <li key={item.label}>
            <button
              type="button"
              aria-pressed={checked[i]}
              onClick={() => setChecked(prev => prev.map((c, idx) => (idx === i ? !c : c)))}
              className="flex min-h-11 w-full items-center gap-3 rounded-bb-md border border-line bg-cream px-3 py-2.5 text-left transition-colors hover:border-green/40"
            >
              <span
                aria-hidden="true"
                className={`flex size-5 shrink-0 items-center justify-center rounded-md border transition-colors duration-200 ${
                  checked[i] ? "border-green bg-green" : "border-line bg-paper"
                }`}
              >
                {checked[i] && (
                  <svg viewBox="0 0 16 16" className="size-3 text-paper" aria-hidden="true">
                    <path
                      d="M3 8.5l3.2 3.2L13 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
              <span className="min-w-0">
                <span
                  className={`block text-sm transition-all duration-200 ${
                    checked[i] ? "text-muted-foreground line-through" : "font-medium text-ink"
                  }`}
                >
                  {item.label}
                </span>
                <span className="block text-[11px] text-ink-2">{item.buy}</span>
              </span>
            </button>
          </li>
        ))}
        {/* The point of the whole panel: the plan already netted this one off. */}
        <li className="flex items-center gap-3 rounded-bb-md border border-line bg-cream px-3 py-2.5">
          <span
            aria-hidden="true"
            className="size-5 shrink-0 rounded-md border border-line bg-paper opacity-50"
          />
          <span className="min-w-0">
            <span className="block text-sm text-muted-foreground">
              {t("landingPage.demo.sItem4")}
            </span>
            <span className="block text-[11px] font-semibold text-green-2">
              {t("landingPage.demo.sCovered")}
            </span>
          </span>
        </li>
      </ul>
    </>
  )
}

function InsightsPanel() {
  const { t } = useI18n()
  return (
    <>
      <PanelHead
        eyebrow={t("landingPage.demo.navInsights")}
        title={t("landingPage.demo.iTitle")}
      />
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-bb-md border border-line bg-cream p-4">
          <p className="text-[10px] font-semibold tracking-wide text-ink-2 uppercase">
            {t("landingPage.demo.iSavedLabel")}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-2">
            <span className="font-display text-3xl leading-none font-semibold text-green">
              {t("landingPage.demo.iSavedValue")}
            </span>
            <span className="inline-flex items-center rounded-bb-full bg-gold/20 px-2 py-0.5 text-[11px] font-medium text-ink-2">
              {t("landingPage.demo.iEstimated")}
            </span>
          </p>
        </div>
        <div className="rounded-bb-md border border-line bg-cream p-4">
          <p className="text-[10px] font-semibold tracking-wide text-ink-2 uppercase">
            {t("landingPage.demo.iLoggedLabel")}
          </p>
          <p className="mt-1 flex items-baseline gap-1.5">
            <span className="font-display text-3xl leading-none font-semibold text-ink">
              {t("landingPage.demo.iLoggedValue")}
            </span>
            <span className="text-sm text-ink-2">{t("landingPage.demo.iLoggedOf")}</span>
          </p>
        </div>
      </div>
    </>
  )
}

export function DemoSection() {
  const { t } = useI18n()
  const reduce = useReducedMotion() ?? false
  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("today")

  return (
    <section id="demo" className="scroll-mt-[12vh] border-b border-line bg-cream">
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
        <Reveal>
          <p className="text-sm font-semibold tracking-wide text-green uppercase">
            {t("landingPage.demo.eyebrow")}
          </p>
          <h2 className="mt-2 text-3xl font-bold text-ink md:text-4xl">
            {t("landingPage.demo.heading")}
          </h2>
          <p className="mt-3 max-w-prose text-ink-2">{t("landingPage.demo.lead")}</p>
        </Reveal>

        <Reveal
          index={1}
          className="mt-10 rounded-bb-xl bg-[#dfe5d7] p-3 shadow-[var(--bb-shadow)] sm:p-4"
        >
          <div className="grid overflow-hidden rounded-bb-lg bg-paper md:grid-cols-[200px_1fr]">
            <aside className="flex flex-col gap-6 bg-green p-5 text-paper">
              <div className="flex items-center gap-2">
                <BrandMarkV2 size={24} />
                <span className="text-sm font-bold">{t("common.appName")}</span>
              </div>
              <nav
                role="tablist"
                aria-label={t("landingPage.demo.panelAria")}
                className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1 md:mx-0 md:flex-col md:overflow-visible md:px-0 md:pb-0"
              >
                {TABS.map(({ id, labelKey, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    id={`demo-tab-${id}`}
                    aria-selected={tab === id}
                    aria-controls="demo-panel"
                    onClick={() => setTab(id)}
                    className={`flex min-h-11 shrink-0 items-center gap-2.5 rounded-bb-md px-3 text-left text-sm font-medium whitespace-nowrap transition-colors ${
                      tab === id ? "bg-paper/15 text-paper" : "text-paper/70 hover:bg-paper/10"
                    }`}
                  >
                    <Icon aria-hidden="true" className="size-4" />
                    {t(labelKey)}
                  </button>
                ))}
              </nav>
              {/* Amy's real entry point on web is the rail / bottom nav — not a
                  banner inside Today. */}
              <p className="mt-auto flex items-center gap-2 rounded-bb-md border border-lime/25 bg-lime/10 px-3 py-2.5 text-sm font-bold text-lime">
                <Sparkles aria-hidden="true" className="size-4" />
                {t("landingPage.demo.askAmy")}
              </p>
            </aside>

            <div
              role="tabpanel"
              id="demo-panel"
              aria-labelledby={`demo-tab-${tab}`}
              className="p-5 sm:p-7"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={tab}
                  initial={reduce ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduce ? undefined : { opacity: 0, y: -8 }}
                  transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                >
                  {tab === "today" && <TodayPanel />}
                  {tab === "plan" && <PlanPanel />}
                  {tab === "kitchen" && <KitchenPanel />}
                  {tab === "shop" && <ShopPanel />}
                  {tab === "insights" && <InsightsPanel />}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}
